import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, UserCog, ClipboardCheck, PackageX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import * as api from '@/services/seasonalWmsApi';

const STATUS_STYLE = {
  generated: 'bg-slate-100 text-slate-700 border-slate-300',
  assigned: 'bg-blue-100 text-blue-700 border-blue-300',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};
const STATUS_LABEL = { generated: 'Generated', assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed' };
const FILTERS = ['all', 'generated', 'assigned', 'in_progress', 'completed'];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const SeasonalBoxPickSlips = () => {
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [counts, setCounts] = useState({});
  const [canManage, setCanManage] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [pickers, setPickers] = useState([]);
  const [assignFor, setAssignFor] = useState(null); // slip being assigned
  const [chosenPicker, setChosenPicker] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Cancel-boxes flow
  const [cancelFor, setCancelFor] = useState(null);   // slip whose boxes we're cancelling
  const [cancelBoxes, setCancelBoxes] = useState([]); // [{box_id, display_no, slug, total_qty, picked}]
  const [cancelSel, setCancelSel] = useState(() => new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [boxesLoading, setBoxesLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async (st = status, q = search) => {
    setLoading(true);
    try {
      const res = await api.getBoxPickSlips(st, q);
      setSlips(res.slips || []);
      setCounts(res.status_counts || {});
      setCanManage(!!res.can_manage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load pick slips');
    } finally { setLoading(false); }
  }, [status, search]);

  useEffect(() => { load('all', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAssign = async (slip) => {
    setAssignFor(slip);
    setChosenPicker(slip.assigned_picker || '');
    try {
      const res = await api.getPickers();
      setPickers(res.pickers || []);
      if (!(res.pickers || []).length) {
        toast.warning('No Seasonal pickers set for today — add them in Available Pickers.');
      }
    } catch { setPickers([]); }
  };

  const confirmAssign = async () => {
    if (!chosenPicker) { toast.error('Select a picker'); return; }
    setAssigning(true);
    try {
      const res = await api.assignBoxPicker(assignFor.pick_slip_id, chosenPicker);
      toast.success(res.message || 'Picker assigned');
      setAssignFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign picker');
    } finally { setAssigning(false); }
  };

  const openCancel = async (slip) => {
    setCancelFor(slip);
    setCancelBoxes([]);
    setCancelSel(new Set());
    setCancelReason('');
    setBoxesLoading(true);
    try {
      const res = await api.getSlipDetail(slip.pick_slip_id);
      setCancelBoxes(res.boxes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load boxes');
    } finally { setBoxesLoading(false); }
  };

  const toggleBox = (boxId) => {
    setCancelSel((prev) => {
      const next = new Set(prev);
      if (next.has(boxId)) next.delete(boxId); else next.add(boxId);
      return next;
    });
  };
  const toggleAllBoxes = () => {
    setCancelSel((prev) => (prev.size === cancelBoxes.length ? new Set() : new Set(cancelBoxes.map((b) => b.box_id))));
  };

  const confirmCancel = async () => {
    if (cancelSel.size === 0) { toast.error('Select at least one box to cancel'); return; }
    if (!cancelReason.trim()) { toast.error('A cancellation reason is required'); return; }
    setCancelling(true);
    try {
      const res = await api.cancelSlipBoxes(cancelFor.pick_slip_id, [...cancelSel], cancelReason.trim());
      toast.success(res.message || 'Boxes cancelled');
      setCancelFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel boxes');
    } finally { setCancelling(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-pickslips-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="sps-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <ClipboardCheck className="w-7 h-7 mr-3 text-violet-400" />
            <div className="flex-1">
              <h1 className="text-xl font-bold font-display">Box Pick Slips</h1>
              <p className="text-sm text-[#E8DCC8]">Seasonal box pick slips &amp; picker assignment</p>
            </div>
            <Button variant="ghost" onClick={() => load()} className="text-[#F9FAFB] hover:bg-white/10" data-testid="sps-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-4 max-w-xl">
            {['generated', 'assigned', 'in_progress', 'completed'].map((k) => (
              <div key={k} className="bg-white/10 rounded-lg px-3 py-2 text-center" data-testid={`sps-count-${k}`}>
                <p className="text-2xl font-bold leading-none">{counts[k] ?? 0}</p>
                <p className="text-[11px] text-[#E8DCC8] mt-1">{STATUS_LABEL[k]}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Select value={status} onValueChange={(v) => { setStatus(v); load(v, search); }}>
            <SelectTrigger className="w-44" data-testid="sps-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => (
                <SelectItem key={f} value={f} data-testid={`sps-status-opt-${f}`}>
                  {f === 'all' ? 'All statuses' : STATUS_LABEL[f]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(status, search); }}
              placeholder="Search slip / customer / DC / picker…"
              className="pl-9"
              data-testid="sps-search-input"
            />
          </div>
          <Button onClick={() => load(status, search)} data-testid="sps-search-btn">Search</Button>
          {(search || status !== 'all') && (
            <Button variant="outline" onClick={() => { setSearch(''); setStatus('all'); load('all', ''); }} data-testid="sps-clear-btn">Clear</Button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E5DFD0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pick Slip</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-center">Boxes</TableHead>
                <TableHead>DC No</TableHead>
                <TableHead>Halnode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned Picker</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slips.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-10" data-testid="sps-empty">
                  {loading ? 'Loading…' : 'No pick slips found.'}
                </TableCell></TableRow>
              ) : slips.map((s) => (
                <TableRow key={s.pick_slip_id} data-testid={`sps-row-${s.pick_slip_id}`}>
                  <TableCell className="font-mono text-xs">{s.pick_slip_id}</TableCell>
                  <TableCell>{s.customer || '—'}</TableCell>
                  <TableCell className="text-center">{s.picked_count}/{s.box_count}</TableCell>
                  <TableCell>{s.dc_number || '—'}</TableCell>
                  <TableCell>{s.halnode_pick_slip_no || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLE[s.status] || ''}>{STATUS_LABEL[s.status] || s.status}</Badge>
                  </TableCell>
                  <TableCell data-testid={`sps-picker-${s.pick_slip_id}`}>
                    {s.assigned_picker_name ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium">{s.assigned_picker_name}</span>
                        {s.assigned_by === 'SYSTEM_AUTO' && <span className="text-[10px] text-emerald-600">(auto)</span>}
                      </span>
                    ) : (
                      <span className="text-rose-500">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{fmt(s.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canManage || s.status === 'completed'}
                        onClick={() => openAssign(s)}
                        data-testid={`sps-assign-btn-${s.pick_slip_id}`}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        {s.assigned_picker ? 'Reassign' : 'Assign'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-rose-200 text-rose-700 hover:bg-rose-50"
                        disabled={!canManage || s.status === 'cancelled' || (s.box_count || 0) === 0}
                        onClick={() => openCancel(s)}
                        data-testid={`sps-cancel-boxes-btn-${s.pick_slip_id}`}
                      >
                        <PackageX className="w-4 h-4 mr-1" />
                        Cancel Boxes
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => { if (!o) setAssignFor(null); }}>
        <DialogContent data-testid="sps-assign-dialog">
          <DialogHeader><DialogTitle>Assign Picker</DialogTitle></DialogHeader>
          {assignFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <div><span className="text-gray-400">Slip:</span> <span className="font-mono">{assignFor.pick_slip_id}</span></div>
                <div><span className="text-gray-400">Customer:</span> {assignFor.customer} · {assignFor.box_count} box(es)</div>
                <div><span className="text-gray-400">Currently:</span>{' '}
                  {assignFor.assigned_picker_name
                    ? <span className="font-medium">{assignFor.assigned_picker_name}{assignFor.assigned_by === 'SYSTEM_AUTO' ? ' (auto)' : ''}</span>
                    : <span className="text-rose-500">Unassigned</span>}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Pick from today's Seasonal pickers</label>
                <Select value={chosenPicker} onValueChange={setChosenPicker}>
                  <SelectTrigger className="mt-1" data-testid="sps-assign-select"><SelectValue placeholder="Select a picker" /></SelectTrigger>
                  <SelectContent>
                    {pickers.length === 0 ? (
                      <SelectItem value="__none" disabled>No pickers set for today</SelectItem>
                    ) : pickers.map((p) => (
                      <SelectItem key={p.staff_id} value={p.staff_id} data-testid={`sps-picker-opt-${p.staff_id}`}>
                        {p.staff_name} ({p.staff_id}) · {p.active_slips || 0} active
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)} data-testid="sps-assign-cancel">Cancel</Button>
            <Button onClick={confirmAssign} disabled={assigning || !chosenPicker || chosenPicker === '__none'} data-testid="sps-assign-confirm">
              {assigning ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel boxes dialog */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => { if (!o) setCancelFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="sps-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageX className="w-5 h-5 text-rose-600" /> Cancel Boxes from Pick Slip
            </DialogTitle>
            <DialogDescription>Select the boxes to return to storage and give a reason.</DialogDescription>
          </DialogHeader>
          {cancelFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <div><span className="text-gray-400">Slip:</span> <span className="font-mono">{cancelFor.pick_slip_id}</span></div>
                <div><span className="text-gray-400">Customer:</span> {cancelFor.customer}{cancelFor.place ? ` — ${cancelFor.place}` : ''} · DC {cancelFor.dc_number || '—'}</div>
              </div>
              <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                Cancelled boxes return to their original bin (available to pick again) and are removed from this slip. If you cancel all boxes, the whole pick slip is cancelled.
              </p>

              <div className="border border-[#E5DFD0] rounded-lg max-h-72 overflow-auto" data-testid="sps-cancel-box-list">
                {boxesLoading ? (
                  <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading boxes…</div>
                ) : cancelBoxes.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">No boxes on this slip.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left w-10">
                          <input type="checkbox" checked={cancelSel.size === cancelBoxes.length && cancelBoxes.length > 0}
                            onChange={toggleAllBoxes} data-testid="sps-cancel-select-all" aria-label="Select all" />
                        </th>
                        <th className="px-3 py-2 text-left font-medium">Box</th>
                        <th className="px-3 py-2 text-left font-medium">Slug</th>
                        <th className="px-3 py-2 text-right font-medium">Qty</th>
                        <th className="px-3 py-2 text-left font-medium">State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelBoxes.map((b) => (
                        <tr key={b.box_id} className="border-t hover:bg-gray-50" data-testid={`sps-cancel-box-${b.box_id}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={cancelSel.has(b.box_id)} onChange={() => toggleBox(b.box_id)}
                              data-testid={`sps-cancel-check-${b.box_id}`} aria-label={`Select ${b.display_no}`} />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{b.display_no || b.box_number}</td>
                          <td className="px-3 py-2">{b.slug || '—'}</td>
                          <td className="px-3 py-2 text-right">{b.total_qty}</td>
                          <td className="px-3 py-2">
                            {b.picked
                              ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">Picked</Badge>
                              : <Badge className="bg-slate-100 text-slate-600 border-0 text-[11px]">Reserved</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Cancellation reason <span className="text-rose-500">*</span></label>
                <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Wrong branch, order short-shipped, customer amendment…"
                  className="mt-1" data-testid="sps-cancel-reason" />
              </div>
              <div className="text-xs text-gray-500" data-testid="sps-cancel-selected-count">
                {cancelSel.size} of {cancelBoxes.length} box(es) selected
                {cancelBoxes.length > 0 && cancelSel.size === cancelBoxes.length && ' — this will cancel the whole pick slip'}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelFor(null)} data-testid="sps-cancel-dialog-close">Close</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmCancel}
              disabled={cancelling || cancelSel.size === 0 || !cancelReason.trim()}
              data-testid="sps-cancel-confirm">
              {cancelling ? 'Cancelling…' : `Cancel ${cancelSel.size || ''} Box(es)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalBoxPickSlips;
